import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { Team } from "@redotech/redo-model/team";
import { stringify } from "csv-stringify/browser/esm/sync";
import { CoverageFilterValue } from "./teams-types";

export const isTeamAnonymized = (team: Team): boolean =>
  team.teamAnonymization?.wasAnonymized === true;

export const generateCSVData = (teams: Team[]): string => {
  const headers = ["Name", "Store URL", "Coverage", "Price"];
  const rows = teams.map((team) => [
    team.name,
    team.storeUrl,
    team.settings.coverageEnabled ? "TRUE" : "FALSE",
    team.pricePerOrder
      ? moneyFormat({ amount: +team.pricePerOrder, currency: Currency.USD })
      : "",
  ]);

  const csvContent = stringify([headers, ...rows]);

  return csvContent;
};

export const parseAndNormalizeStoreUrl = (storeUrl: string): URL | null => {
  if (!storeUrl || typeof storeUrl !== "string") {
    return null;
  }

  const trimmedUrl = storeUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  try {
    const urlWithProtocol =
      trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")
        ? trimmedUrl
        : `https://${trimmedUrl}`;

    return new URL(urlWithProtocol);
  } catch {
    return null;
  }
};

export const isValidStoreUrl = (storeUrl: string): boolean => {
  const url = parseAndNormalizeStoreUrl(storeUrl);
  return url !== null && url.hostname.length > 0;
};

export const buildShopifyAdminUrl = (storeUrl: string): string | null => {
  const url = parseAndNormalizeStoreUrl(storeUrl);
  return url ? `${url.protocol}//${url.hostname}/admin` : null;
};

export const buildCSVFilename = (
  search: string,
  coverageFilter: CoverageFilterValue,
): string => {
  const date = Temporal.Now.plainDateISO().toString();
  const parts = ["merchants", date];

  if (search.trim()) {
    // Sanitize search term for filename (remove special characters, limit length)
    const sanitizedSearch = search
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .substring(0, 20);
    parts.push(`search-${sanitizedSearch}`);
  }

  if (coverageFilter !== "all") {
    parts.push(`coverage-${coverageFilter}`);
  }

  return parts.join("-");
};
